import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { Select } from "@app/components/select/select";
import Table from "@app/components/table";
import { ColumnConfig, Order } from "@app/components/table/table.component";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  LIST_BANK,
  OrderType,
  SystemMessage,
} from "@app/constants";
import AffiliateService from "@app/services/http/affiliate.service";
import { Meta } from "@app/types";
import {
  CreateTransferPayload,
  TransferHistory,
  TransferStatus,
} from "@app/types/affiliate.type";
import { createTransferValidationSchema } from "@app/validations/affiliate.validation";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { RequestParams } from "@core/services/http/http.type";
import clsx from "clsx";
import { Form, Formik, FormikContextType } from "formik";
import { createRef, useEffect } from "react";
import { useState } from "react";

const getStatus = (status: TransferStatus) => {
  switch (status) {
    case TransferStatus.PROCESSING:
      return {
        text: "Đang chờ",
        className: "text-[#4558FF] bg-[#CCE7FF]",
      };
    case TransferStatus.SUCCESS:
      return {
        text: "Đã hoàn thành",
        className: "text-[#00A653] bg-[#E6FFED]",
      };
    case TransferStatus.TIMEOUT:
      return {
        text: "Hết hạn",
        className: "text-[#FE7E20] bg-[#FFF0E6]",
      };
    case TransferStatus.ERROR:
      return {
        text: "Thất bại",
        className: "text-[#FC4040] bg-[#FFEBEB]",
      };
    default:
      return {
        text: "",
        className: "",
      };
  }
};

function CreatedDateTable({ data }: { data: TransferHistory }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div>{data.createdDate}</div>
    </div>
  );
}

function AmountTable({ data }: { data: TransferHistory }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div>{data.amount}</div>
    </div>
  );
}

function RemainingAmountTable({ data }: { data: TransferHistory }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div>{data.remainingAmount}</div>
    </div>
  );
}

function BankNumberTable({ data }: { data: TransferHistory }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div>{data.bankNumber}</div>
    </div>
  );
}

function StatusTable({ data }: { data: TransferHistory }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div className={clsx(getStatus(data.status).className, "rounded-[8px] px-[16px] py-[8px]")}>{getStatus(data.status).text}</div>
    </div>
  );
}

function AffiliateUser() {
  const formRef = createRef<FormikContextType<CreateTransferPayload>>();
  const [histories, setHistories] = useState<TransferHistory[]>([]);
  const [meta, setMeta] = useState<Meta>();
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    AffiliateService.getHistory(requestParams, abortController.signal).then(
      (res) => {
        setHistories(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [requestParams]);

  const columnConfig: ColumnConfig[] = [
    {
      label: "Thời gian",
      dataKey: "createdDate",
      width: "180px",
      component: CreatedDateTable,
      hasOrder: true,
    },
    {
      label: "Số tiền rút",
      dataKey: "amount",
      width: "180px",
      component: AmountTable,
      hasOrder: true,
    },
    {
      label: "Số dư còn lại",
      dataKey: "remainingAmount",
      width: "180px",
      component: RemainingAmountTable,
      hasOrder: true,
    },
    {
      label: "Số tài khoản nhận",
      dataKey: "bankNumber",
      width: "180px",
      component: BankNumberTable,
      hasOrder: true,
    },
    {
      label: "Trạng thái",
      dataKey: "status",
      width: "180px",
      component: StatusTable,
      hasOrder: true,
    },
  ];

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };


  const onSubmit = (values: CreateTransferPayload) => {
    AffiliateService.createTransfer(values).then(() => {
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      formRef.current?.setValues({
        bankName: "",
        bankNumber: "",
        bankOwner: "",
        amount: 0,
      });
      formRef.current?.resetForm();
    });
  };

  return (
    <div className="mt-[60px] font-[SVN-Gilroy]">
      <div className="flex flex-col gap-4">
        <div className="px-[20px] py-[20px] rounded-[16px] bg-[#FFFFFF] w-full">
          <Formik
            innerRef={formRef}
            initialValues={{
              bankName: "",
              bankNumber: "",
              bankOwner: "",
              amount: 0,
            }}
            onSubmit={onSubmit}
            validateOnBlur
            validateOnChange
            validationSchema={createTransferValidationSchema}
            enableReinitialize
          >
            <Form>
              <div className="grid grid-cols-2 gap-4 xl:gap-[72px]">
                <div>
                  <div className="font-semibold text-sm font-[SVN-Gilroy]">
                    Tên ngân hàng
                  </div>
                  <FormControl name="bankName">
                    <Select
                      placeholder="Tên ngân hàng"
                      options={LIST_BANK.map((bank) => ({
                        label: bank,
                        value: bank,
                      }))}
                      name="bankName"
                      className="mt-1 !w-full !rounded-xl !h-12"
                      classNameSelect="!w-full !bg-[#EFF1F999] font-[SVN-Gilroy] font-medium !leading-12 !rounded-xl"
                    />
                  </FormControl>
                  <div className="font-semibold text-sm font-[SVN-Gilroy] mt-[10px]">
                    Tên chủ tài khoản
                  </div>
                  <FormControl name="bankOwner">
                    <Input
                      className="mt-1 !w-full"
                      inputClassName="!bg-[#EFF1F999] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                    />
                  </FormControl>
                </div>
                <div>
                  <div className="font-semibold text-sm font-[SVN-Gilroy]">
                    Số tài khoản
                  </div>
                  <FormControl name="bankNumber">
                    <Input
                      className="mt-1 !w-full"
                      inputClassName="!bg-[#EFF1F999] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                    />
                  </FormControl>
                  <div className="font-semibold text-sm font-[SVN-Gilroy] mt-[10px]">
                    Nhập số tiền cần rút
                  </div>
                  <FormControl name="amount">
                    <Input
                      className="mt-1 !w-full"
                      inputClassName="!bg-[#EFF1F999] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                    />
                  </FormControl>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  width="auto"
                  label="Xác nhận"
                  labelClassName="font-[SVN-Gilroy] tracking-[0.028px]"
                  className="min-w-[183px] py-[12px] px-[24px] !h-[52px] flex items-center justify-center !rounded-[12px] !bg-[#5570F1]"
                />
              </div>
            </Form>
          </Formik>
        </div>

        <div className="w-full mt-[36px]">
          <p className="font-semibold text-[20px] text-[#171A20] font-[SVN-Gilroy]">Lịch sử rút tiền</p>
          <div className="mt-6 rounded-xl bg-white py-6 px-5">
            <div className="mt-6 overflow-x-auto">
              <Table
                columnConfig={columnConfig}
                dataTable={histories}
                meta={meta}
                hasAction={false}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                onRowClickOrder={onOrderChange}
                tableClassName="w-full"
                componentEmpty={
                  <div className="text-center py-3 bg-[#f4f4f4]">
                    <p className="text-[#666] text-xl">Không có dữ liệu</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AffiliateUser;
